import { faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

const ModalVincularXmlEmitido = ({ show, onHide, idOrden, onLinked }) => {
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);
    const pageSize = 10;
    const [hasMore, setHasMore] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [facturas, setFacturas] = useState([]);
    const [loadingFacturas, setLoadingFacturas] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const timerRef = useRef(null);

    useEffect(() => {
        if (show) {
            // reset state
            setSearchTerm('');
            setSelectedIndex(null);
            setPageNumber(1);
            loadFacturas(1);
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            loadFacturas(pageNumber);
        }
    }, [pageNumber]);

    const loadFacturas = async (pageNum = 1) => {
        setLoadingFacturas(true);
        try {
            // Intentar obtener facturas emitidas (se espera que el endpoint exista)
            const resp = await fetch('/api/contabilidad/centro-xml/FacturasEmitidas/ObtenerFacturasEmitidasParaRelacion', {
                method: 'POST',
                headers: {
                    'Authorization': token ? 'Bearer ' + token : '',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ pageNumber: pageNum, pageSize: pageSize, searchTerm })
            }).catch(() => null);

            if (!resp) {
                setFacturas([]);
                setLoadingFacturas(false);
                return;
            }

            const data = await resp.json().catch(() => null);
            if (resp.ok && data) {
                // Expecting the response shape: { iResponseType:1, vMessage:..., detalles: "{\"table1\":[�],\"table2\":[�],\"table3\":[�]}" }
                // table1 -> operation result (iResponseType, vMessage)
                // table2 -> rows (facturas)
                // table3 -> { totalRecords }
                let dsObj = null;
                try {
                    if (data.detalles) {
                        dsObj = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                    } else {
                        // fallback: maybe backend returned DataSet directly
                        dsObj = data;
                    }
                } catch (e) {
                    dsObj = null;
                }

                if (!dsObj) {
                    setFacturas([]);
                    showErrorToast('Respuesta inv�lida del servidor');
                } else {
                    // Validate operation result in table1
                    const op = Array.isArray(dsObj.table1) && dsObj.table1.length > 0 ? dsObj.table1[0] : (Array.isArray(dsObj.Table1) && dsObj.Table1.length > 0 ? dsObj.Table1[0] : null);
                    if (!op || op.iResponseType !== 1) {
                        const msg = op && op.vMessage ? op.vMessage : 'Error al obtener facturas';
                        setFacturas([]);
                        showErrorToast(msg);
                    } else {
                        // Extract rows from table2
                        const rows = Array.isArray(dsObj.table2) ? dsObj.table2 : (Array.isArray(dsObj.Table2) ? dsObj.Table2 : (Array.isArray(dsObj[1]) ? dsObj[1] : []));
                        // Extract totalRecords from table3
                        let total = 0;
                        const t3 = dsObj.table3 || dsObj.Table3 || dsObj[2];
                        if (Array.isArray(t3) && t3.length > 0) {
                            const r = t3[0];
                            if (r.totalRecords !== undefined) total = Number(r.totalRecords) || 0;
                            else if (r.TotalRecords !== undefined) total = Number(r.TotalRecords) || 0;
                            else {
                                const firstVal = Object.values(r)[0];
                                total = Number(firstVal) || 0;
                            }
                        }

                        const arr = Array.isArray(rows) ? rows : [];
                        setFacturas(arr);
                        setTotalRecords(total);
                        const pages = Math.max(1, Math.ceil((total || arr.length) / pageSize));
                        setTotalPages(pages);
                        setHasMore(pageNum < pages);
                    }
                }
            } else {
                setFacturas([]);
                if (data && data.vMessage) showErrorToast(data.vMessage);
            }
        } catch (err) {
            setFacturas([]);
            showErrorToast('Error al obtener facturas: ' + err);
        } finally {
            setLoadingFacturas(false);
        }
    };

    const handleVincular = async () => {
        // determine currently displayed list (after filter)
        const displayed = (facturas || []).filter(f => {
            if (!searchTerm) return true;
            const q = searchTerm.toString().toLowerCase();
            const idFactura = (f.idFacturaEmitida ?? '').toString().toLowerCase();
            const uuidVal = (f.vUUIDFactura ?? '').toString().toLowerCase();
            const cliente = (renderCliente(f) || '').toString().toLowerCase();
            return idFactura.includes(q) || uuidVal.includes(q) || cliente.includes(q);
        }).slice(0, 50);

        const selected = selectedIndex !== null ? displayed[selectedIndex] : null;
        if (!selected) {
            showErrorToast('Seleccione una factura de la lista antes de vincular');
            return;
        }
        const uuidToSend = selected.vUUIDFactura || '';
        if (!uuidToSend) {
            showErrorToast('La factura seleccionada no tiene UUID v�lido');
            return;
        }
        setLoading(true);
        try {
            // prefer idFacturaEmitida field from selected row
            const idFacturaEmitida = selected.idFacturaEmitida ?? 0;
            const body = { idFacturaEmitida: idFacturaEmitida, idOrden: idOrden };
            const resp = await fetch('/api/contabilidad/centro-xml/FacturasEmitidas/GuardarRelacionOrdenFactura', {
                method: 'POST',
                headers:
                {
                    'Content-Type': 'application/json',
                    'Authorization': token ? 'Bearer ' + token : ''
                },
                body: JSON.stringify(body)
            });

            const data = await resp.json().catch(() => null);
            if (resp.ok && data && data.iResponseType === 1) {
                showSuccessToast(data.vMessage || 'Factura vinculada correctamente');
                onLinked && onLinked(data);
                onHide && onHide();
            } else {
                const msg = (data && data.vMessage) || `Error al vincular factura (${resp.status})`;
                showErrorToast(msg);
            }
        } catch (err) {
            showErrorToast('Error al vincular factura: ' + (err.message || err));
        } finally {
            setLoading(false);
        }
    };

    const renderCliente = (f) => {
        return f.vRazonSocial || f.vRazonSocialReceptor || f.vNombreCliente || f.vNombre || '';
    };
    const renderTotal = (f) => {
        const v = f.dTotal ?? f.dTotalSinFormato ?? f.dTotalOriginal ?? f.dTotalConFormato ?? f.dImporteTotal;
        if (v === undefined || v === null) return '';
        // si viene como string, intentar parse
        const num = Number(v);
        if (!isNaN(num)) return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return String(v);
    };
    const renderMoneda = (f) => {
        return f.vMoneda || f.vMonedaFactura || f.moneda || '';
    };

    // A�adir efecto para debounce en searchTerm
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        // iniciar nuevo timer
        timerRef.current = setTimeout(() => {
            // realizar b�squeda despu�s de 300ms de inactividad
            loadFacturas(1);
            setPageNumber(1);
        }, 300);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [searchTerm]);

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Vincular XML emitido con la orden #{idOrden}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-2">
                        <Form.Label>Buscar facturas (id, UUID, cliente)</Form.Label>
                        <Form.Control
                            type="text"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setSelectedIndex(null); }}
                            placeholder="Buscar..."
                        />
                    </Form.Group>

                    <div className="text-muted small mb-2">Puede buscar una factura por id, UUID o cliente.</div>

                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {loadingFacturas ? (
                            <div className="text-center py-3"><Spinner animation="border" /></div>
                        ) : (
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: 36 }}>Sel</th>
                                        <th>#</th>
                                        <th>idFactura</th>
                                        <th>UUID</th>
                                        <th>Cliente</th>
                                        <th className="text-end">Total</th>
                                        <th>Moneda</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(facturas && facturas.length > 0) ? (
                                        // aplicar filtro local por searchTerm
                                        facturas
                                            .filter(f => {
                                                if (!searchTerm) return true;
                                                const q = searchTerm.toString().toLowerCase();
                                                const idFactura = (f.idFacturaEmitida).toString().toLowerCase();
                                                const uuidVal = (f.vUUIDFactura).toString().toLowerCase();
                                                const cliente = (renderCliente(f) || '').toString().toLowerCase();
                                                return idFactura.includes(q) || uuidVal.includes(q) || cliente.includes(q);
                                            })
                                            .slice(0, 50)
                                            .map((f, idx) => {
                                                const idFactura = f.idFacturaEmitida ?? '';
                                                const uuidVal = f.vUUIDFactura ?? '';
                                                const isSelected = selectedIndex === idx;
                                                const rowStyle = {
                                                    cursor: 'pointer',
                                                    background: isSelected ? '#cfe9ff' : undefined,
                                                    fontSize: isSelected ? '0.78rem' : undefined
                                                };

                                                return (
                                                    <tr key={idx} onClick={() => { setSelectedIndex(idx); }} style={rowStyle}>
                                                        <td className="text-center align-middle" style={{ width: 36 }}>
                                                            {isSelected ? (
                                                                <FontAwesomeIcon icon={faCheckCircle} className="text-primary" />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faCircle} className="text-muted" />
                                                            )}
                                                        </td>
                                                        <td style={{ fontSize: 10 }}>{idx + 1}</td>
                                                        <td style={{ fontSize: 10 }}>{idFactura}</td>
                                                        <td style={{ fontSize: 10 }}>{uuidVal}</td>
                                                        <td style={{ fontSize: 11 }}>{renderCliente(f)}</td>
                                                        <td className="text-end" style={{ fontSize: 10 }}>{renderTotal(f)}</td>
                                                        <td style={{ fontSize: 10 }}>{renderMoneda(f)}</td>
                                                    </tr>
                                                );
                                            })
                                    ) : (
                                        <tr><td colSpan={6} className="text-center text-muted">No hay facturas disponibles</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                    {/* Paginaci�n simple */}
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <small className="text-muted">P�gina {pageNumber} de {totalPages} (Total: {totalRecords})</small>
                        </div>
                        <div>
                            <Button variant="outline-secondary" size="sm" className="me-2" disabled={pageNumber <= 1} onClick={() => { setPageNumber(prev => Math.max(1, prev - 1)); setSelectedIndex(null); }}>
                                Anterior
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={!hasMore} onClick={() => { setPageNumber(prev => prev + 1); setSelectedIndex(null); }}>
                                Siguiente
                            </Button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>Cancelar</Button>
                <Button variant="primary" onClick={handleVincular} disabled={loading}>
                    {loading ? <><Spinner animation="border" size="sm" />&nbsp;Vinculando...</> : 'Vincular factura'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalVincularXmlEmitido.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    idOrden: PropTypes.number.isRequired,
    onLinked: PropTypes.func
};

export default ModalVincularXmlEmitido;
